"use client"

import { useLayoutEffect } from "react"
import { THEME_INIT_SCRIPT } from "@/lib/theme-init-script"

/**
 * Deliberately NOT "tls-theme-init". On the client-rendered 404 document React
 * *does* put the layout's `<script id="tls-theme-init">` in the DOM — it just
 * never executes it — so guarding on that id would skip the injection in the
 * one case this component exists for.
 */
const SCRIPT_ID = "tls-theme-init-fallback"

/**
 * Runs the pre-paint theme bootstrap on documents where it never got the
 * chance — which is every `notFound()` render.
 *
 * ## The bug this exists for
 *
 * `app/layout.tsx` emits THEME_INIT_SCRIPT as a plain inline `<script>` in
 * `<head>`, so it executes during head parsing and `<html>` carries `.light`
 * before the first paint. That works on every normal route.
 *
 * It does not work on a 404 reached by `notFound()` at render time. Next.js
 * serves those from `getErrorRSCPayload`, whose document is literally
 * `<html id="__next_error__"><head></head><body></body></html>`: the root
 * layout is in the RSC flight payload, not in the HTML. Measured on a
 * production build of this repo, `/sounds/not-a-real-thing` returns a hard 404
 * whose served HTML contains **zero** stylesheet links and no `<script
 * id="tls-theme-init">` — the whole page, layout included, is rendered by React
 * on the client. React does not execute a `dangerouslySetInnerHTML` script it
 * renders (that is the same React 19 constraint noted in `app/layout.tsx`), so
 * the theme bootstrap silently never runs and a light-theme visitor gets a dark
 * 404. Verified by screenshot before and after.
 *
 * ## Why it re-injects the script rather than reimplementing it
 *
 * A dynamically created `<script>` with a text body DOES execute. Appending the
 * canonical THEME_INIT_SCRIPT keeps exactly one copy of the logic — including
 * its MutationObserver, which re-asserts the class when React's later passes
 * strip it (TES-1578, bug 2). Reimplementing the class toggle here would give
 * the 404 a second, subtly different theme resolver to drift from.
 *
 * `useLayoutEffect` so the class lands before the browser paints this commit.
 *
 * Routes that 404 at the ROUTER instead (every `dynamicParams = false` family)
 * already render the real root layout and its real, executing inline script.
 * There this re-runs the same idempotent program a second time: `apply()`
 * returns early when the class already agrees with the stored preference, so
 * the only cost is one extra MutationObserver on a page that is, by
 * definition, a dead end.
 */
export function ThemeInitFallback() {
  useLayoutEffect(() => {
    if (document.getElementById(SCRIPT_ID)) return
    const script = document.createElement("script")
    script.id = SCRIPT_ID
    script.textContent = THEME_INIT_SCRIPT
    document.head.appendChild(script)
  }, [])

  return null
}
